const canonicalPermissionClaimType = 'permission';
const roleClaimTypes = [
  'role',
  'roles',
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
] as const;

export type PermissionClaims = Record<string, unknown>;

interface KeycloakResourceAccess {
  [audience: string]: {
    roles?: unknown;
  };
}

interface KeycloakOrganizationClaim {
  resource_access?: unknown;
}

interface KeycloakOrganizations {
  [organization: string]: KeycloakOrganizationClaim;
}

export function getPermissionClaims(
  claims: PermissionClaims | null | undefined,
  permissionClaimAudience?: string,
): readonly string[] {
  if (claims == null) {
    return [];
  }

  const permissions = new Set<string>();

  for (const permission of getClaimValues(
    claims[canonicalPermissionClaimType],
  )) {
    permissions.add(permission);
  }

  for (const claimType of roleClaimTypes) {
    for (const permission of getClaimValues(claims[claimType])) {
      permissions.add(permission);
    }
  }

  if (permissionClaimAudience != null) {
    for (const permission of getKeycloakResourceAccessRoles(
      claims['resource_access'],
      permissionClaimAudience,
    )) {
      permissions.add(permission);
    }

    for (const permission of getOrganizationResourceAccessRoles(
      claims['organization'],
      permissionClaimAudience,
    )) {
      permissions.add(permission);
    }
  }

  return [...permissions];
}

function getKeycloakResourceAccessRoles(
  resourceAccessClaim: unknown,
  permissionClaimAudience: string,
): readonly string[] {
  if (
    resourceAccessClaim == null ||
    typeof resourceAccessClaim !== 'object' ||
    Array.isArray(resourceAccessClaim)
  ) {
    return [];
  }

  const resourceAccess = resourceAccessClaim as KeycloakResourceAccess;
  return getClaimValues(resourceAccess[permissionClaimAudience]?.roles);
}

function getClaimValues(value: unknown): readonly string[] {
  if (typeof value === 'string') {
    return [value];
  }

  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === 'string');
  }

  return [];
}

function getOrganizationResourceAccessRoles(
  organizationClaim: unknown,
  permissionClaimAudience: string,
): readonly string[] {
  if (
    organizationClaim == null ||
    typeof organizationClaim !== 'object' ||
    Array.isArray(organizationClaim)
  ) {
    return [];
  }

  const organizations = organizationClaim as KeycloakOrganizations;
  const permissions = new Set<string>();

  for (const organization of Object.values(organizations)) {
    for (const permission of getKeycloakResourceAccessRoles(
      organization.resource_access,
      permissionClaimAudience,
    )) {
      permissions.add(permission);
    }
  }

  return [...permissions];
}
